import React, { useRef, useEffect, useState } from "react";
import { cls } from "@wss/cls";
import { Popover, Icon, usePopover } from "@clarkinc/zest-design-system";
import { QuantityDiscount } from "~/common/Store/models/ProductListing";
import { calculateEndQuantity, getEndQuantity } from "./QuantityDiscountHelper";
import spritePath from "~/common/Icons/Standard/standard-sprite.svg";
import QuantityDiscountV2TableRow from "./QuantityDiscountTableRow";
import useIsMounted from "~/common/useIsMounted";

interface QuantityDiscountsTableProps {
  productPrice: number;
  quantityDiscounts: QuantityDiscount[];
  hasLotDiscounts: boolean;
  startBuy: number;
  mustBuy: number;
  productListingView?: string;
  selectedProductItemNumber: string;
  unitOfMeasure: string;
  displaySalePrice?: boolean;
  divideByUnit?: string;
  divideQuantity?: number;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
  productItemLink: string;
}

const QuantityDiscountsTable = ({
  productPrice,
  quantityDiscounts,
  hasLotDiscounts,
  startBuy,
  mustBuy,
  selectedProductItemNumber,
  unitOfMeasure,
  displaySalePrice = false,
  divideByUnit = "",
  divideQuantity = 0,
  productItemLink,
  productListingView,
  setIsQuantityDiscountPopoverOpen,
}: QuantityDiscountsTableProps) => {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (document) {
      const zestCloseButton = document.querySelectorAll(
        '[data-testid="quantityDiscountV2"] [aria-label="Close popover"]',
      );
      if (navigator.maxTouchPoints > 0 || "ontouchstart" in window) {
        setIsMobileDevice(true);
      } else {
        zestCloseButton.forEach((button) => {
          button.classList.add("hidden");
        });
      }
    }
  }, []);

  const mounted = useIsMounted();
  const popover = usePopover();
  const popoverId = `popover-quantity-discount-${selectedProductItemNumber}`;
  const getStartQuantityFromQuantityDiscount =
    quantityDiscounts[0]!.startQuantity - 1;
  const endQty = getEndQuantity(mustBuy, getStartQuantityFromQuantityDiscount);
  const formattedEndQty = calculateEndQuantity(
    mustBuy,
    getStartQuantityFromQuantityDiscount,
  );
  const shouldDisplayEnd =
    getStartQuantityFromQuantityDiscount <= startBuy ||
    formattedEndQty === startBuy;
  const getEndQuantityForFirstQuantityDiscount = cls(
    startBuy,
    !shouldDisplayEnd && `- ${endQty}`,
  );

  const firstQuantityDiscountRowData = hasLotDiscounts
    ? "Reg."
    : getEndQuantityForFirstQuantityDiscount;

  const timeOutRef = useRef<ReturnType<typeof setTimeout>>();

  const wishlistPage = window?.location?.pathname.includes("wishlist") ?? false;

  const handleMouseEnter = () => {
    if (timeOutRef.current && !isMobileDevice) {
      clearTimeout(timeOutRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobileDevice && mounted) {
      timeOutRef.current = setTimeout(() => {
        popover.close(popoverId);
      }, 100);
    }
  };

  const handlePointerEnter = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && !isMobileDevice && mounted) {
      popover.toggle(popoverId);
    }
  };

  const handleOnClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    popoverId: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (isPopoverOpen) {
      popover.close(popoverId);
      setIsPopoverOpen(false);
    } else {
      popover.open(popoverId);
      setIsPopoverOpen(true);
    }

    if (!isMobileDevice && productItemLink) {
      window.location.href = productItemLink;
    }
  };

  const QuantityDiscountsButtonContent = () => {
    return (
      <div className={cls("flex")}>
        <p
          className={cls(
            "text-xs",
            "xsl:text-2xs",
            productListingView === "list" ? "md:text-xs" : "xs:text-xs",
            "text-gray-800",
            "m-0",
            "font-semibold",
            "group-hover:text-green-700",
            "group-active:text-green-700",
            "flex",
            "items-center",
          )}
        >
          Buy More, Save More
        </p>
      </div>
    );
  };

  // This is needed because the setup tooling used to create quantity discounts doesn't support any error handling when startQuantity is less than startBuy so we have to do the check here instead.
  let updatedQuantityDiscount: QuantityDiscount[] = [];
  let quantityDiscountsModified = false;
  if (quantityDiscounts.length > 0) {
    updatedQuantityDiscount = quantityDiscounts.map((quantity) => {
      const updateStartQuantity =
        !quantity.isLotDiscount && quantity.startQuantity < startBuy;
      if (updateStartQuantity) {
        quantityDiscountsModified = true;
      }
      return {
        isLotDiscount: quantity.isLotDiscount,
        price: quantity.price,
        quantity: quantity.quantity,
        startQuantity: updateStartQuantity ? startBuy : quantity.startQuantity,
        endQuantity: quantity.endQuantity,
      };
    });
  }

  return updatedQuantityDiscount.length > 0 ? (
    <Popover
      placement="top"
      popover={popover}
      data-testid="quantityDiscountV2"
      className={cls(
        "pt-3",
        wishlistPage ? "justify-center" : "",
        mounted ? "" : "quantityDiscountsPopoverV2Wrapper",
      )}
      onOpen={() => {
        if (setIsQuantityDiscountPopoverOpen !== undefined) {
          setIsQuantityDiscountPopoverOpen(selectedProductItemNumber);
        }
      }}
    >
      {/* The pointer and mouse events on the zest popover component are one off and should not be used like this for this component. This should use the default intended use which is a onClick. */}
      <Popover.Trigger
        className="z-20 cursor-pointer"
        contentId={popoverId}
        onClick={(event) => handleOnClick(event, popoverId)}
        {...(!isMobileDevice && {
          onPointerEnter: handlePointerEnter,
          onMouseLeave: () => popover.close(popoverId),
          onMouseEnter: () => popover.open(popoverId),
          onFocus: () => popover.open(popoverId),
          onBlur: () => popover.close(popoverId),
        })}
        tabIndex={0}
      >
        <div
          className={cls(
            "flex",
            "items-center",
            "group",
            "bg-gray-50",
            "border",
            "border-dashed",
            "border-gray-300",
            "hover:border-green-200",
            "hover:bg-green-50",
            "rounded",
            "px-1.5",
            "xsl:px-1",
            "sm:px-1.5",
            "py-1",
          )}
        >
          <div
            className={cls("flex items-center", { "flex-row": isMobileDevice })}
          >
            <Icon
              aria-label="more information"
              name="dollar-circle"
              spriteSheetHref={spritePath}
              className={cls(
                "fill-current",
                "h-[14px]",
                "w-[14px]",
                "pr-1",
                "xsl:pr-0.5",
                "xs:pr-1",
                "text-green-500",
                "group-hover:text-green-700",
                "group-active:text-green-700",
              )}
            />
            {isMobileDevice && <QuantityDiscountsButtonContent />}
          </div>
          {!isMobileDevice && <QuantityDiscountsButtonContent />}
        </div>
      </Popover.Trigger>
      <Popover.Content
        id={popoverId}
        title="Quantity Discounts"
        placement="top"
        headerProps={{
          className: cls(
            "pl-3",
            mounted ? "" : "quantityDiscountsPopoverV2Header",
          ),
        }}
        className={cls(
          "p-0",
          "w-full",
          "min-w-[180px]",
          "xxs:min-w-[190px]",
          "[@media(min-width:1750px)]:w-[200px]",
        )}
        wrapperProps={{
          onBlur: () => popover.close(popoverId),
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }}
      >
        <table
          data-testid="quantity-discounts-table"
          className={cls(
            "mt-0",
            "min-w-[180px]",
            "xxs:min-w-[190px]",
            "[@media(min-width:1750px)]:w-[200px]",
            "shadow-none",
            "bg-transparent",
          )}
        >
          <tbody>
            {!quantityDiscountsModified ? (
              <QuantityDiscountV2TableRow
                unitOfMeasure={unitOfMeasure}
                mustBuy={mustBuy}
                displaySalePrice={displaySalePrice}
                divideByUnit={divideByUnit}
                divideQuantity={divideQuantity}
                quantityDiscountsModified={!quantityDiscountsModified}
                firstQuantityDiscountRowData={firstQuantityDiscountRowData}
                productPrice={productPrice}
              />
            ) : null}
            {quantityDiscounts.map((quantity) => {
              return (
                <QuantityDiscountV2TableRow
                  key={quantity.price}
                  quantity={quantity}
                  unitOfMeasure={unitOfMeasure}
                  mustBuy={mustBuy}
                  displaySalePrice={displaySalePrice}
                  divideByUnit={divideByUnit}
                  divideQuantity={divideQuantity}
                  productPrice={productPrice}
                />
              );
            })}
          </tbody>
        </table>
      </Popover.Content>
    </Popover>
  ) : null;
};

export default QuantityDiscountsTable;
